import { useState } from "react";
import { ProductBadge } from "src/components/Common/ProductBadge/ProductBadge";
import { isTest, ProductKey } from "src/config/config";
import { useRouter } from "src/hooks/useRouter";
import { useRecoilState } from "recoil";
import { dspProductAtom, productKeyAtom, userInfoAtom } from "src/atoms";
import useProductInfo, {
  CategoryType,
  isDspProductList,
} from "src/mocks/useProductInfo";
import styles from "./index.module.scss";
import { setProductToSessionStorage } from "src/utils/storage/auth";
import { useAuth } from "src/hooks/useAuth";
import { useIsOpenSelectProduct } from "src/hooks/recoil/useIsOpenSelectProduct";
import { useProductFlags } from "src/hooks/recoil/useProductFlags";
import { useTranslation } from "react-i18next";

// 탭 타입 정의
interface TabType {
  title: string;
  category: CategoryType;
}

const Home = () => {
  const [activeTab, setActiveTab] = useState<CategoryType>("all");
  const [productKey, setProductKey] = useRecoilState(productKeyAtom);
  const [dspProduct, setDspProduct] = useRecoilState(dspProductAtom);
  const { goLogin, goConsole } = useRouter();
  // const { cloudUserInfo } = useAuth();
  const [cloudUserInfo] = useRecoilState(userInfoAtom);
  const { isOpenSelectProduct, setIsOpenSelectProduct } =
    useIsOpenSelectProduct();
  const { isDspProduct } = useProductFlags();
  const { products } = useProductInfo();
  const { t } = useTranslation();
  const tabs: TabType[] = [
    { title: t("home.tabs.all"), category: "all" },
    {
      title: t("home.tabs.dataSecurity"),
      category: "data-security",
    },
    {
      title: t("home.tabs.documentManagement"),
      category: "document-management",
    },
    {
      title: t("home.tabs.supplyChainSecurity"),
      category: "supply-chain-security",
    },
    {
      title: t("home.tabs.privacyDetection"),
      category: "privacy-detection",
    },
    {
      title: t("home.tabs.cloudSecurity"),
      category: "cloud-security",
    },
    { title: t("home.tabs.phishingTraining"), category: "phishing-training" },
    {
      title: t("home.tabs.secureMessenger"),
      category: "secure-messenger",
    },
  ];
  // if (isTest) {
  //   // Fireside
  //   tabs.push(
  //     { title: t("home.tabs.phishingTraining"), category: "phishing-training" },
  //     {
  //       title: t("home.tabs.secureMessenger"),
  //       category: "secure-messenger",
  //     }
  //   );
  // }
  type ProductInfoKey =
    | "MIND_SAT"
    | "WRAPSODY"
    | "WRAPSODY_ECO"
    | "FIRESIDE"
    | "DSPM"
    | "FED"
    | "FSS"
    | "FSP"
    | "FC_BR"
    | "FDR";
  // const { product, cardInfo } = useProductInfo();

  // 현재 선택된 탭의 category 정보 찾기
  const currentTabCategory =
    tabs.find((tab) => tab.category === activeTab)?.category || "all";

  // 선택된 탭의 category에 따라 제품 필터링
  const filteredProducts = products.filter((product) => {
    if (currentTabCategory === "all") {
      return true;
    }
    return product.categories.includes(currentTabCategory);
  });

  const handleConsultingClick = () => {
    // 도입 컨설팅 받기 버튼 클릭 처리
    // console.log("도입 컨설팅 받기 클릭");
    window.open(
      "https://www.fasoo.com/contact-us/solutions-b?type=fasoo-dsp-cloud",
      "_blank"
    );
  };

  const handleProductAction = (
    productId: string,
    productKey: ProductKey | undefined,
    actionType: string,
    url?: string
  ) => {
    if (url) {
      // URL이 있는 경우 새창으로 열기
      window.open(url, "_blank");
    } else {
      switch (actionType) {
        case "product":
          // productKey가 유효한지 확인
          if (!productKey) {
            console.error("ProductKey is undefined for product:", productId);
            return;
          }

          // DSP 제품군인지 확인하여 세션 스토리지 설정
          // console.log("Product selection:", {
          //   productId,
          //   productKey,
          //   isDspProduct: isDspProductList.includes(productKey as string),
          //   isDspProductList,
          // });
          if (isDspProductList.includes(productKey as string)) {
            setProductToSessionStorage("DSP");
            setProductKey(productKey as ProductKey);
            setDspProduct(productKey);
          } else {
            setProductToSessionStorage(productKey as string);
            setProductKey(productKey as ProductKey);
            setDspProduct(productKey);
          }
          // if (isDspProductList.includes(productKey as string)) {
          //   // console.log("Setting DSP product to session storage");
          //   setProductKey(productKey);
          //   setProductToSessionStorage("DSP");
          // } else {
          //   console.log(
          //     "Setting individual product to session storage:",
          //     productKey
          //   );
          //   setProductToSessionStorage(productKey as string);
          //   setProductKey(productKey);
          // }

          // 로그인 상태에 따른 라우팅
          if (!cloudUserInfo) {
            goLogin();
          } else {
            setIsOpenSelectProduct(false);
            goConsole();
          }
          break;
        case "primary":
          break;
        default:
          console.log(`${productId} - ${actionType} 클릭`);
          break;
      }
    }
  };

  return (
    <div className={styles.homeContainer}>
      {/* 메인 콘텐츠 */}
      <div className={styles.mainContent}>
        <h1 className={styles.mainTitle}>{t("home.title")}</h1>
        <p className={styles.mainDescription}>{t("home.subTitle.1")}</p>
        <p className={styles.mainDescription}>{t("home.subTitle.2")}</p>

        <button
          className={styles.consultingBtn}
          onClick={handleConsultingClick}
        >
          {t("home.consultingBtn")}
        </button>
      </div>
      {/* 상단 탭 네비게이션 */}
      <div className={styles.headerTabs}>
        <div className={styles.tabList}>
          {tabs.map((tab) => (
            <div
              key={tab.title}
              className={`${styles.tabItem} ${
                activeTab === tab.category ? styles.active : ""
              } ${tab.category === "all" && styles.all}`}
              onClick={() => setActiveTab(tab.category)}
            >
              {tab.title}
            </div>
          ))}
        </div>
      </div>
      {/* 제품 그리드 */}
      <div className={styles.productsGrid}>
        {filteredProducts.map((product) => (
          <div
            key={product.id}
            className={`${styles.productCard} ${
              filteredProducts.length === 1 ? styles.single : ""
            }`}
          >
            <div className={styles.productCardInner}>
              <div className={styles.productCardContent}>
                <div
                  className={`${styles.productIcon} ${
                    styles[`icon-${product.id}`]
                  }`}
                >
                  <img src={product.icon} alt={`${product.name} 아이콘`} />
                </div>

                <div className={styles.productTitle}>
                  {product?.badge &&
                    product.badge.map((badge) => (
                      <ProductBadge key={badge} type={badge} />
                    ))}
                  {product.name}
                </div>

                <p className={styles.productDescription}>
                  {product.description}
                </p>
              </div>
              <div className={styles.productActions}>
                {product?.moreInfoUrl?.[0] && (
                  <button
                    className={styles.btnPrimary}
                    onClick={() =>
                      window.open(product.moreInfoUrl?.[0]?.url, "_blank")
                    }
                  >
                    {t("home.productInfo.btn.learnMore")}
                  </button>
                )}
                {product.productKey && (
                  <button
                    className={styles.btnProduct}
                    onClick={() =>
                      handleProductAction(
                        product.id,
                        product.productKey as ProductKey,
                        "product"
                      )
                    }
                  >
                    {t("home.productInfo.btn.select")}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Home;
